"use client"

import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Check, X } from "lucide-react"
import type { FieldConfiguration } from "@/types/workflow"
import { CustomDropdownField } from "@/components/custom-dropdown-field"
import { ModernDatePicker } from "@/components/modern-date-picker"
import { useLanguage } from '@/context/language-context'
import { getLocalizedField } from '@/utils/language-utils'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { useToast } from '@/hooks/use-toast'
import { useCodeValues } from '@/hooks/use-code-values'
import { isDateTimeField, isEmailField, matchesFieldFormatting } from '@/utils/column-type-helpers'
import { isValidEmailFormat } from '@/utils/email-helpers'
import { useTranslation } from '@/hooks/use-translation'

interface EditableCellProps {
  value: any
  column: FieldConfiguration
  rowId: string
  onSave: (value: any) => void
  onCancel: () => void
}

export function EditableCell({ value, column, rowId, onSave, onCancel }: EditableCellProps) {
  const [editValue, setEditValue] = useState(value)
  const { language } = useLanguage()
  const { trackCellAction } = useWorkflowTracking()
  const { toast } = useToast()
  const codeValues = useCodeValues()
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // Parse UTC date string to preserve exact date
  const parseUTCDate = (dateStr: string) => {
    if (!dateStr) return null
    const dateOnly = dateStr.split('T')[0]
    return dateOnly
  }

  // Format date for saving - convert local midnight to UTC
  const formatDateForSave = (dateStr: string | null) => {
    if (!dateStr) return null
    // Parse as local date at midnight
    const [year, month, day] = dateStr.split('-').map(Number)
    const localDate = new Date(year, month - 1, day, 0, 0, 0, 0)
    // Convert to ISO string (which is in UTC)
    return localDate.toISOString()
  }

  const handleSave = (rawValue: any) => {
    // Trim BEFORE validating, not just before saving. Surrounding whitespace is never meaningful in
    // these cells and is usually a paste artefact, but " a@b.com " fails the email pattern and any
    // fieldFormatting regex anchored with ^...$ — so without this the user is blocked by a validation
    // error they cannot see, on a value that is actually correct.
    //
    // Strings only: date/boolean/number branches pass non-strings through here, and a whitespace-only
    // string collapses to "" which the backend stores as null (an empty value is a clear, not a value).
    const newValue = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

    // Email format check, independent of fieldFormatting.
    //
    // matchesFieldFormatting below only runs when the column carries a fieldFormatting regex, and the
    // SYSTEM email columns do not — they have no CustomFieldInputTypeCode, and MapInputFormatToColumnType
    // (WorkflowHubV2Service) only yields a pattern for the Email and Date input types.
    //
    // Custom Email fields DO get one, so this check is not what covers them. edit-field-modal writes
    // `fieldFormatting: (inputType === "Date") ? fieldFormatting : null`, but the backend OVERRIDES that
    // null with the platform-owned address pattern on both the create path and — as of this change — the
    // field-config update path, specifically so the regex survives an edit.
    //
    // So the two checks split the population: the regex covers custom email fields, this check covers the
    // system ones. Net effect before it existed: any value at all saved into WorkerEmail et al.
    //
    // Pattern is shared with client-request-modal's hiringManagerEmail check so the grid and the modal
    // agree on what counts as an email. Blank is allowed — clearing a field is not a format error.
    if (isEmailField(column) && !isValidEmailFormat(newValue)) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: t('invalidEmailFormat', {
          field: getLocalizedField(column, 'columnName', language),
        }),
      });
      return;
    }

    // Validate field format before saving
    if (!matchesFieldFormatting(column, newValue)) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: `Invalid format for ${getLocalizedField(column, 'columnName', language)}`,
      });
      return;
    }

    // Track the cell edit
    trackCellAction('edit', column.columnCode)
    onSave(newValue)
  }

  const handleCancel = () => {
    // Track the cancel action (using edit action type since cancel is not in the allowed types)
    trackCellAction('edit', column.columnCode)
    onCancel()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave(editValue)
    } else if (e.key === "Escape") {
      handleCancel()
    }
  }

  // Tenant code-table-driven dropdown — fires regardless of ColumnTypeCode
  // when the field config has a CodeValueSource set. The display-side
  // (grid-cell.tsx) already resolves the cell text via
  // codeValues.getCodeValueTextByCode for the same source, so saving the
  // CODE keeps it in lockstep — the display will pick up the new code's
  // text from the cached table without a refetch.
  //
  // Single-select dropdown (multiSelect=false) because every code field
  // we surface this way is scalar. Searchable since some tables (currencies,
  // job titles) are long. allowClear so users can unset back to null on
  // optional fields — required ones still validate on save server-side.
  if (column.codeValueSource && codeValues.isReady) {
    const options = codeValues
      .getCodeValuesSortByText(column.codeValueSource, false)
      .map((c: { code: string; text: string }) => ({
        value: c.code,
        label: c.text,
      }))
    // The stored value is the code (e.g. "USD"). CustomDropdownField wants
    // selectedValues as string[] — wrap/unwrap so single-select still uses
    // its multi-select machinery without weirdness.
    const selectedValues = editValue ? [String(editValue)] : []

    return (
      <div className="flex items-center gap-1">
        <div className="min-w-[180px]">
          <CustomDropdownField
            options={options}
            selectedValues={selectedValues}
            onChange={(values) => {
              // Auto-save on selection for single-select dropdowns. UX
              // matches "click option = done" — same pattern the workflow
              // hub uses for status dropdowns. setEditValue is a no-op
              // visually (we exit edit mode immediately) but keeps the
              // local state consistent for any consumer reading editValue
              // between selection and unmount. Pass newValue directly to
              // handleSave because setEditValue is async.
              const newValue = values[0] ?? null
              setEditValue(newValue)
              handleSave(newValue)
            }}
            multiSelect={false}
            showSearch={true}
            allowClear={true}
          />
        </div>
        {/* No Check button — auto-save on pick. X stays for "I opened the
            dropdown but changed my mind without selecting" (clicks the
            cell elsewhere also work via the outside-click handler the
            CustomDropdownField has). */}
        <div className="flex items-center">
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  // Handle different column types based on customValuesType
  switch (column.customValuesType?.toLowerCase() || column.columnTypeCode?.toLowerCase()) {
    case "text":
    case "email":
    case "link":
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 min-w-[120px]"
          />
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )

    case "date":
    case "datetime":
      // Both columnTypeCode='Date' and 'DateTime' land here (lowercased above).
      // The switch key prefers `customValuesType` and falls back to
      // `columnTypeCode`, so a custom field defined with customValuesType='Date'
      // matches the 'date' case while a system column with columnTypeCode='DateTime'
      // matches 'datetime' — without the second label, DateTime fields silently
      // dropped to the default text-input branch and the user got a plain
      // editable string instead of the picker.
      //
      // Render the editor in an overlay that escapes the cell's box so the
      // confirm/cancel buttons can never get clipped by a narrow column
      // (~240px is the minimum width to fit the 180px picker + two icon
      // buttons; common date columns are narrower). An outside-click listener
      // on the wrapper cancels the edit when the user clicks anywhere else
      // — matches every other editor in the app and prevents the previous
      // "stuck in edit mode" foot-gun where the user thought a click outside
      // would dismiss the picker.
      //
      // DateTime branch: when the field is DateTime (columnTypeCode='datetime'
      // OR fieldFormatting='DateTime'), we skip the parseUTCDate/formatDateForSave
      // helpers — those strip the time portion and re-emit local-midnight as
      // UTC, which would silently drop the hours/minutes the user picked. The
      // DateCellEditor + ModernDatePicker pair handle ISO 8601 round-trip on
      // the showTime path: incoming value is parsed in the user's local tz,
      // the picker emits an ISO 8601 UTC string on each edit, and display
      // sites (formatDateTime) convert UTC back to local for rendering.
      {
        const isDateTime = isDateTimeField(column);
        if (isDateTime) {
          return (
            <DateCellEditor
              value={value || null}
              onSave={(iso) => handleSave(iso)}
              onCancel={handleCancel}
              showTime
            />
          )
        }
        return (
          <DateCellEditor
            value={parseUTCDate(value)}
            onSave={(date) => handleSave(formatDateForSave(date))}
            onCancel={handleCancel}
          />
        )
      }

    // Custom boolean slots arrive as columnTypeCode 'Boolean' (no customValuesType);
    // without this label they fell through to the default text <Input> and emitted the
    // STRING "true" — which ATS's System.Text.Json JsonPatch refuses to coerce into
    // bool? (→ 400). Sharing the yesno <Switch> makes them emit a real JS boolean.
    case "yesno":
    case "boolean":
      // editValue can arrive as a real boolean OR a stringified "true"/"false" (the grid
      // pre-stringifies cell values), so parse defensively. onCheckedChange always sets a
      // genuine boolean, so handleSave sends a boolean regardless of the initial shape.
      {
        const isChecked = editValue === true || String(editValue).toLowerCase() === 'true'
        return (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={isChecked}
              onCheckedChange={(checked) => setEditValue(checked)}
              id={`boolean-switch-${rowId}-${column.fieldConfigurationId}`}
            />
            <label htmlFor={`boolean-switch-${rowId}-${column.fieldConfigurationId}`} className="text-sm">
              {isChecked ? "Yes" : "No"}
            </label>
          </div>
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        )
      }

    case "dropdown":
      // For dropdown fields with customValues
      if (column.customValues && column.customValues.length > 0) {
        // Format options for the dropdown
        const dropdownOptions = column.customValues.map((customValue) => ({
          value: getLocalizedField(customValue, 'title', language),
          label: getLocalizedField(customValue, 'title', language),
          color: customValue.color,
        }))

        // Convert single value to array for multi-select (if needed)
        const selectedValues = Array.isArray(editValue) ? editValue : editValue ? [editValue] : []

        return (
          <div className="flex items-center gap-1">
            <div className="min-w-[180px]">
              <CustomDropdownField
                options={dropdownOptions}
                selectedValues={selectedValues}
                onChange={(values) => setEditValue(values)}
                useColorCode={true}
                showSearch={false}
              />
            </div>
            <div className="flex items-center">
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
                <Check className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )
      }

      // Fallback for dropdowns without customValues
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 min-w-[120px]"
          />
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )

    // All numeric ColumnTypeCodes share the numeric editor. The seeded system
    // columns use "Double" (e.g. BillRate) while custom fields may use
    // "Number"/"Decimal"/"Money"; without listing every alias the field fell
    // through to the default text branch and behaved as a plain string input
    // (and "Double" never got type="number"). Keep this list in sync with
    // isNumberField() and grid-cell.tsx's numericTypes.
    case "number":
    case "double":
    case "decimal":
    case "money":
    case "float":
    case "int":
    case "integer":
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            type="number"
            // `?? ""` not `|| ""` — a legitimate 0 is falsy and would otherwise
            // render as an empty field (and read back as "can't set anything").
            value={editValue ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              // Convert empty string to null, otherwise convert to number
              const numericValue = value === "" ? null : Number(value);
              setEditValue(numericValue);
            }}
            onKeyDown={handleKeyDown}
            className="h-8 min-w-[120px]"
          />
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )

    default:
      // Default to text input for unknown types
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={editValue || ""}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 min-w-[120px]"
          />
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )
  }
}

interface DateCellEditorProps {
  value: string | null
  onSave: (value: string | null) => void
  onCancel: () => void
  /**
   * When true, render the ModernDatePicker in DateTime mode (calendar +
   * HH:MM time inputs). The picker emits a full ISO 8601 UTC string on every
   * change; the editor passes that through to onSave as-is. The wider overlay
   * (280px vs the default 240px) gives the time row room to breathe without
   * wrapping under the calendar's footer buttons.
   */
  showTime?: boolean
}

/**
 * Inline date editor that renders the picker + confirm/cancel buttons in a
 * portalled overlay anchored to the cell. Why a portal: cells in narrow
 * columns clip overflow, which previously hid the confirm/cancel icons.
 * Rendering through document.body escapes the clipping. Clicking anywhere
 * outside the overlay (or the picker popover, which is itself portalled by
 * Radix) cancels the edit — matching the dismiss behavior of every other
 * inline editor.
 */
function DateCellEditor({ value, onSave, onCancel, showTime = false }: DateCellEditorProps) {
  const [editValue, setEditValue] = useState<string | null>(value)
  const anchorRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  // Anchor the overlay to the cell's current screen position on mount, then
  // clamp it into the viewport so the confirm/cancel icons are never clipped.
  // For a column at the far-right edge of the table the raw cell.left would
  // push the ~280px overlay off-screen to the right and hide the Check button
  // (the exact bug users hit editing the last custom-field column). If the
  // overlay would overflow the right/bottom edge we shift it back left/up by
  // the overflow, effectively flipping it toward the side that has room, and
  // never let it go past the top/left edge either. Fixed positioning means a
  // page scroll won't move it, which is fine: the user picks and confirms in
  // one gesture and a scroll blurs/closes the editor anyway.
  useEffect(() => {
    if (!anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    const margin = 8
    // overlayRef isn't mounted on this first pass (the portal renders only
    // once position is set), so fall back to the overlay's known minWidth /
    // single-row height — both match the rendered box closely enough to keep
    // the buttons on-screen.
    const overlayWidth = overlayRef.current?.offsetWidth ?? (showTime ? 280 : 240)
    const overlayHeight = overlayRef.current?.offsetHeight ?? 40
    let left = rect.left
    if (left + overlayWidth + margin > window.innerWidth) {
      left = window.innerWidth - overlayWidth - margin
    }
    left = Math.max(margin, left)
    let top = rect.top
    if (top + overlayHeight + margin > window.innerHeight) {
      top = window.innerHeight - overlayHeight - margin
    }
    top = Math.max(margin, top)
    setPosition({ top, left })
  }, [showTime])

  // Outside-click dismiss. Defer attachment by a tick so the click that
  // opened this editor (which bubbles after mount) doesn't immediately
  // close it. Exclude clicks inside the Radix popper wrapper because the
  // date picker calendar lives there, portalled out of the cell.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (overlayRef.current && overlayRef.current.contains(target)) return
      // Allow interaction with the picker popover (portalled by Radix)
      const poppers = document.querySelectorAll('[data-radix-popper-content-wrapper]')
      for (const popper of Array.from(poppers)) {
        if (popper.contains(target)) return
      }
      onCancel()
    }
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handler)
    }, 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handler)
    }
  }, [onCancel])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSave(editValue)
    } else if (e.key === 'Escape') {
      onCancel()
    }
  }

  return (
    <>
      {/* Anchor stays inside the cell so we can read the cell's bounding rect. */}
      <div ref={anchorRef} className="h-8 min-w-[120px]" />
      {position !== null && typeof document !== 'undefined' && createPortal(
        <div
          ref={overlayRef}
          className="fixed z-[60] flex items-center gap-1 rounded-md border border-border bg-background p-1 shadow-lg"
          style={{ top: position.top, left: position.left, minWidth: showTime ? 280 : 240 }}
          onKeyDown={handleKeyDown}
        >
          <div className={showTime ? "min-w-[220px]" : "min-w-[180px]"}>
            <ModernDatePicker
              value={editValue}
              onChange={(date) => setEditValue(date)}
              autoOpen
              highZIndex
              showTime={showTime}
            />
          </div>
          <div className="flex items-center">
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onSave(editValue)}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}